import type { FC } from "react"
import { memo } from "react"
import {
	IconCheckOutline18 as Check,
	IconUserOutline18 as UserIcon,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { PopoverProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Button, Popover, useBreakpoints } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { Owner } from "./icons/owner"

interface OwnerSelectProps {
	item: {
		id: string | null
		name: string | null
		avatarUrl?: string | null
	} | null
	onChange: (input: {
		id: string | null
		name: string | null
		avatarUrl: string | null
	}) => void
	className?: string
	modal?: PopoverProps["modal"]
}

export const OwnerSelect: FC<OwnerSelectProps> = memo(
	({ item, onChange, className, modal }) => {
		const { role } = useRole()
		const [open, onOpenChange] = useToggle(false)
		const { isMobile } = useBreakpoints()
		const workspace = useZeroWorkspace()

		const teamId = workspace?.defaultLinearTeamId ?? ""

		const membersQuery = api.linear.members.useQuery(
			{ teamId },
			{
				enabled: !!teamId,
				cacheTime: Infinity,
				staleTime: Infinity,
			},
		)

		const members = [...(membersQuery.data ?? [])].sort((a, b) =>
			a.displayName.localeCompare(b.displayName),
		)

		return (
			<Popover
				open={open}
				onOpenChange={(o) => {
					if (role !== "ADMIN") return
					onOpenChange(o)
				}}
				modal={modal}
				className="p-0"
				side={isMobile ? "bottom" : "left"}
				align="start"
				content={
					<Command>
						<CommandInput autoFocus placeholder="Set owner ..." />
						<CommandList>
							<CommandGroup>
								{item?.id && (
									<CommandItem
										onSelect={() => {
											onChange({ id: null, name: null, avatarUrl: null })
											onOpenChange(false)
										}}
									>
										<div className="flex items-center gap-2">
											<UserIcon className="h-4 w-4 text-secondary" />
											<span className="flex-1 truncate text-secondary">
												No owner
											</span>
										</div>
									</CommandItem>
								)}
								{members.map((member) => (
									<CommandItem
										key={member.id}
										onSelect={() => {
											onChange({
												id: member.id,
												name: member.displayName,
												avatarUrl: member.avatarUrl ?? null,
											})
											onOpenChange(false)
										}}
									>
										<div className="flex items-center gap-2">
											{member.avatarUrl ? (
												<img
													src={member.avatarUrl}
													alt={member.displayName}
													className="h-4 w-4 rounded-full object-cover"
												/>
											) : (
												<UserIcon className="h-4 w-4 text-secondary" />
											)}
											<span className="flex-1 truncate">
												{member.displayName}
											</span>
											{item?.id === member.id && <Check className="h-4 w-4" />}
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				}
			>
				<div
					className={cn(
						"flex h-7 items-center text-sm text-secondary",
						className,
					)}
				>
					<Button
						variant="ghost"
						className={cn(
							"w-full max-w-none justify-start text-xs hover:bg-gray-150 dark:hover:bg-gray-800",
							{
								"!text-tertiary": !item?.name,
								"!text-primary": item?.name,
							},
						)}
						Icon={
							item?.avatarUrl
								? () => (
										<img
											src={item.avatarUrl!}
											alt={item.name ?? ""}
											className="h-4 w-4 rounded-full object-cover"
										/>
									)
								: Owner
						}
					>
						{item?.name ?? "Set owner"}
					</Button>
				</div>
			</Popover>
		)
	},
)
